import {
  QueryClient,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  useMutation,
  type UseMutationOptions,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type Batcher, create as createBatcher, windowedFiniteBatchScheduler } from '@yornaath/batshit';
import { useEffect } from 'react';

import { AuthzService } from '../services/api';
import { useCloudPlatformDiscovery, useUser } from '../utils/hooks';
import {
  addTagsToWorkspace,
  archiveWorkspace,
  batchResolvePermissions,
  createWorkspace,
  deleteWorkspace,
  FixedResolvePermissionRequest,
  getUser,
  getWorkspace,
  listAllWorkspaces,
  listGroups,
  listRoleBindings,
  listTags,
  listUsers,
  listWorkspaces,
  removeTagsFromWorkspace,
  unarchiveWorkspace,
  undeleteWorkspace,
  updateWorkspace,
} from './authz.functions';
import {
  type ResolvePermissionRequest,
  type ResolvePermissionResponse,
} from './gen/ai/h2o/authorization/v1/query_api_pb';
import type {
  ListRoleBindingsRequest,
  ListRoleBindingsResponse,
} from './gen/ai/h2o/authorization/v1/role_binding_api_pb';
import type { ListGroupsRequest, ListGroupsResponse } from './gen/ai/h2o/user/v1/group_api_pb';
import type { ListUsersRequest, ListUsersResponse } from './gen/ai/h2o/user/v1/user_api_pb';
import { type User, userName } from './gen/ai/h2o/user/v1/user_pb';
import type { ListTagsRequest, ListTagsResponse } from './gen/ai/h2o/workspace/v1/tag_api_pb';
import type {
  AddTagsRequest,
  AddTagsResponse,
  ArchiveWorkspaceResponse,
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  DeleteWorkspaceResponse,
  ListAllWorkspacesRequest,
  ListAllWorkspacesResponse,
  ListWorkspacesRequest,
  ListWorkspacesResponse,
  RemoveTagsRequest,
  RemoveTagsResponse,
  UndeleteWorkspaceResponse,
  UpdateWorkspaceRequest,
  UpdateWorkspaceResponse,
} from './gen/ai/h2o/workspace/v1/workspace_api_pb';
import { Workspace, workspaceName } from './gen/ai/h2o/workspace/v1/workspace_pb';

// Constants
export const defaultAppstoreWorkspaceName = 'workspaces/appstore';
export const defaultGlobalWorkspaceName = 'workspaces/global';

// AuthzService hook
export const useAuthzService = () => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  useEffect(() => {
    AuthzService.setBasePath(authzUrl);
  }, [authzUrl]);

  return AuthzService;
};

export const cacheKeys = {
  groupsAll: (params?: Pick<ListGroupsRequest, `filter`>) => {
    const baseKey: QueryKey = [`authz`, `groups`, `all`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = `` } = params;
    return baseKey.concat({ filter });
  },
  groupsOne: (groupId: string) => {
    return [`authz`, `groups`, `one`, groupId] as const;
  },
  tagsAll: () => {
    const baseKey: QueryKey = [`authz`, `tags`, `all`];
    return baseKey;
  },
  permissionOne: (params: Pick<Required<ResolvePermissionRequest>, `action` | `resource` | `subject`>) =>
    [...cacheKeys.permissionAllForResource(params.resource), params.subject, params.action] as const,
  permissionAllForResource: (resource: string) => {
    return [`authz`, `permissions`, resource] as const;
  },
  roleBindingsAll: (params?: Pick<ListRoleBindingsRequest, `filter`>) => {
    const baseKey: QueryKey = [`authz`, `roleBindings`, `all`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = `` } = params;
    return baseKey.concat({ filter });
  },
  userAll: (params?: Pick<ListUsersRequest, `filter`>) => {
    const baseKey: QueryKey = [`authz`, `users`, `all`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = `` } = params;
    return baseKey.concat({ filter });
  },
  userOne: (userId: string) => [`authz`, `users`, `one`, userId] as const,
  workspacesAll: (params?: Pick<ListWorkspacesRequest, `filter` | `orderBy` | `showArchived` | `showDeleted`>) => {
    const baseKey: QueryKey = [`authz`, `workspaces`, `all`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = ``, orderBy = ``, showArchived = false, showDeleted = false } = params;
    return baseKey.concat({ filter, orderBy, showArchived, showDeleted });
  },
  workspacesMy: (params?: Pick<ListWorkspacesRequest, `filter` | `orderBy` | `showArchived` | `showDeleted`>) => {
    const baseKey: QueryKey = [`authz`, `workspaces`, `my`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = ``, orderBy = ``, showArchived = false, showDeleted = false } = params;
    return baseKey.concat({ filter, orderBy, showArchived, showDeleted });
  },
  workspaceOne: (workspaceId: string) => {
    return [`authz`, `workspaces`, `one`, workspaceId] as const;
  },
};

// Groups

export const useQueryGroups = <TData = ListGroupsResponse>(
  params: Omit<ListGroupsRequest, `pageToken`> = {},
  options?: Omit<
    UseInfiniteQueryOptions<ListGroupsResponse, unknown, TData>,
    `getNextPageToken` | `queryKey` | `queryFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.groupsAll(params),
    queryFn: ({ pageParam, signal }) => listGroups(authzUrl, { ...params, pageToken: pageParam }, { signal }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

// Helpers

const invalidateWorkspacesCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: cacheKeys.workspacesAll(), exact: false });
  queryClient.invalidateQueries({ queryKey: cacheKeys.workspacesMy(), exact: false });
};

const useWorkspaceStatusMutation = <TResponse>(
  mutation: (authzUrl: string, name: string) => Promise<TResponse>,
  options?: Omit<UseMutationOptions<TResponse, unknown, string>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || ``;
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (name: string) => {
      const response = await mutation(authzUrl, name);
      invalidateWorkspacesCache(queryClient);
      const { workspace: workspaceId } = workspaceName.parse(name);
      queryClient.invalidateQueries({ queryKey: cacheKeys.workspaceOne(workspaceId), exact: true });
      return response;
    },
  });
};

// Tags

export const useQueryTags = <TData = ListTagsResponse>(
  params: Omit<ListTagsRequest, `pageToken`> = {},
  options?: Omit<UseInfiniteQueryOptions<ListTagsResponse, unknown, TData>, `getNextPageToken` | `queryKey` | `queryFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.tagsAll(),
    queryFn: ({ pageParam, signal }) => listTags(authzUrl, { ...params, pageToken: pageParam }, { signal }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

// Permissions

/**
 * A cache object for batchers, structured by Authz base path (should be constant, but there is no strong guarantee)
 * The top-most WeakMap is here just to isolate batchers in tests.
 */
type PermissionsBatcher = Batcher<
  Map<FixedResolvePermissionRequest, ResolvePermissionResponse>,
  FixedResolvePermissionRequest,
  ResolvePermissionResponse
>;
const permissionBatcherCache = new WeakMap<QueryClient, Map<string, PermissionsBatcher>>();

const getPermissionBatcher = (queryClient: QueryClient, basePath: string) => {
  if (!permissionBatcherCache.has(queryClient)) {
    permissionBatcherCache.set(queryClient, new Map());
  }
  const queryClientBatchers = permissionBatcherCache.get(queryClient)!;
  if (!queryClientBatchers.has(basePath)) {
    queryClientBatchers.set(
      basePath,
      createBatcher({
        fetcher: async (requests: FixedResolvePermissionRequest[]) => {
          const { responses } = await batchResolvePermissions(basePath, { requests });
          const requestResponseEntries = requests.map((req, i) => [req, responses![i]] as const);
          return new Map(requestResponseEntries);
        },
        resolver: (batchedResponse, request) => batchedResponse.get(request)!,
        scheduler: windowedFiniteBatchScheduler({
          windowMs: 10,
          maxBatchSize: 1_000,
        }),
      })
    );
  }
  return queryClientBatchers.get(basePath)!;
};

export const getQueryOptionsPermission = <TData = ResolvePermissionResponse>(
  queryClient: QueryClient,
  basePath: string,
  req: FixedResolvePermissionRequest
): UseQueryOptions<ResolvePermissionResponse, unknown, TData> => {
  return {
    staleTime: 120 * 1_000, // consider permissions valid for 120 seconds after the last fetch
    queryKey: cacheKeys.permissionOne(req),
    queryFn: () => {
      return getPermissionBatcher(queryClient, basePath).fetch(req);
    },
  };
};

export const useQueryPermission = <TData = ResolvePermissionResponse>(
  req: FixedResolvePermissionRequest,
  options?: Omit<UseQueryOptions<ResolvePermissionResponse, unknown, TData>, `queryKey` | `queryFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  const baseOptions = getQueryOptionsPermission<TData>(queryClient, authzUrl, req);
  return useQuery({ ...options, ...baseOptions });
};

export const useQueryPermissionCurrentUser = <TData = ResolvePermissionResponse>(
  req: Omit<FixedResolvePermissionRequest, `subject`>,
  options?: Omit<UseQueryOptions<ResolvePermissionResponse, unknown, TData>, `queryKey` | `queryFn`>
) => {
  const currentUser = useUser();
  const currentUserName = userName.compile({ user: currentUser.id });
  return useQueryPermission<TData>({ ...req, subject: currentUserName }, options);
};

/** Query multiple permissions for the current user in parallel */
export const useQueryMultiplePermissionsCurrentUser = <TData = ResolvePermissionResponse>(
  reqs: Omit<FixedResolvePermissionRequest, 'subject'>[],
  options?: Omit<UseQueryOptions<ResolvePermissionResponse, unknown, TData>, 'queryKey' | 'queryFn'>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  const currentUser = useUser();
  const currentUserName = userName.compile({ user: currentUser.id });

  const queries = reqs.map((req) => ({
    ...getQueryOptionsPermission<TData>(queryClient, authzUrl, { ...req, subject: currentUserName }),
    ...options,
  }));

  return useQueries({ queries });
};

export const useQueryRoleBindings = <TData = ListRoleBindingsResponse>(
  request: Omit<ListRoleBindingsRequest, `pageToken`> = {},
  options?: Omit<
    UseInfiniteQueryOptions<ListRoleBindingsResponse, unknown, TData>,
    `getNextPageToken` | `queryKey` | `queryFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.roleBindingsAll(request),
    queryFn: ({ pageParam, signal }) => listRoleBindings(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

// Users

export const getQueryOptionsUser = <TData = User>(
  authzUrl: string,
  userId: string,
  queryClient?: QueryClient // if provided, will search query cache for the initial data, which might speed up the loading significantly
): UseQueryOptions<User, unknown, TData> => {
  const name = userName.compile({ user: userId });
  return {
    initialData:
      queryClient &&
      (() => {
        const cachedListResponses = queryClient.getQueriesData<{ pages: ListUsersResponse[] }>({
          queryKey: cacheKeys.userAll(),
          exact: false,
        });
        for (const [, queryData] of cachedListResponses) {
          if (queryData?.pages) {
            for (const cachedResponse of queryData.pages) {
              const cachedUser = cachedResponse?.users?.find((u) => u.name === name);
              if (cachedResponse) return cachedUser;
            }
          }
        }
        return undefined;
      }),
    queryKey: cacheKeys.userOne(userId),
    queryFn: async ({ signal }) => {
      const response = await getUser(authzUrl, { name }, { signal });
      return response.user!;
    },
  };
};

export const useQueryUser = <TData = User>(userId?: string, options?: UseQueryOptions<User, unknown, TData>) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  const queries = !userId
    ? []
    : [
        {
          ...getQueryOptionsUser<TData>(authzUrl, userId, queryClient),
          ...options,
        },
      ];
  return useQueries({ queries }).at(0);
};

export const useQueryUsers = <TData = ListUsersResponse>(
  request: Omit<ListUsersRequest, `pageToken`> = {},
  options?: Omit<
    UseInfiniteQueryOptions<ListUsersResponse, unknown, TData>,
    `getNextPageToken` | `queryKey` | `queryFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.userAll(request),
    queryFn: ({ pageParam, signal }) => listUsers(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

// Workspaces

export const useMutationCreateWorkspace = (
  options?: Omit<UseMutationOptions<CreateWorkspaceResponse, unknown, CreateWorkspaceRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: CreateWorkspaceRequest) => {
      const response = await createWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      return response;
    },
  });
};

export const useMutationDeleteWorkspace = (
  options?: Omit<UseMutationOptions<DeleteWorkspaceResponse, unknown, string>, `mutationFn`>
) => {
  return useWorkspaceStatusMutation((authzUrl, name) => deleteWorkspace(authzUrl, name), options);
};

export const useMutationUndeleteWorkspace = (
  options?: Omit<UseMutationOptions<UndeleteWorkspaceResponse, unknown, string>, `mutationFn`>
) => {
  return useWorkspaceStatusMutation((authzUrl, name) => undeleteWorkspace(authzUrl, name), options);
};

export const useMutationArchiveWorkspace = (
  options?: Omit<UseMutationOptions<ArchiveWorkspaceResponse, unknown, string>, `mutationFn`>
) => {
  return useWorkspaceStatusMutation((authzUrl, name) => archiveWorkspace(authzUrl, name), options);
};

export const useMutationUnarchiveWorkspace = (
  options?: Omit<UseMutationOptions<UndeleteWorkspaceResponse, unknown, string>, `mutationFn`>
) => {
  return useWorkspaceStatusMutation((authzUrl, name) => unarchiveWorkspace(authzUrl, name), options);
};

export const useMutationUpdateWorkspace = (
  options: Omit<UseMutationOptions<UpdateWorkspaceResponse, unknown, UpdateWorkspaceRequest>, `mutationFn`> = {}
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request) => {
      if (!request.workspace?.name) {
        throw new Error(`Workspace name is required for update`);
      }
      const response = await updateWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      const { workspace: workspaceId } = workspaceName.parse(request.workspace.name);
      // Update the cache for the workspace detail query with returned data synchronously
      queryClient.setQueryData<Workspace>(cacheKeys.workspaceOne(workspaceId), response.workspace);
      // Invalidate the permission query for the workspace
      queryClient.invalidateQueries({
        queryKey: cacheKeys.permissionAllForResource(request.workspace.name),
        exact: false,
      });
      // Invalidate also the role-bindings which can change after workspace update
      queryClient.invalidateQueries({
        queryKey: cacheKeys.roleBindingsAll(),
        exact: false,
      });
      return response;
    },
  });
};

export const useMutationAddTagsToWorkspace = (
  options?: Omit<UseMutationOptions<AddTagsResponse, unknown, AddTagsRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: AddTagsRequest) => {
      const response = await addTagsToWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      if (request.workspace) {
        const { workspace: workspaceId } = workspaceName.parse(request.workspace);
        queryClient.setQueryData<Workspace>(cacheKeys.workspaceOne(workspaceId), response.workspace);
      }
      return response;
    },
  });
};

export const useMutationRemoveTagsFromWorkspace = (
  options?: Omit<UseMutationOptions<RemoveTagsResponse, unknown, RemoveTagsRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: RemoveTagsRequest) => {
      const response = await removeTagsFromWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      if (request.workspace) {
        const { workspace: workspaceId } = workspaceName.parse(request.workspace);
        queryClient.setQueryData<Workspace>(cacheKeys.workspaceOne(workspaceId), response.workspace);
      }
      return response;
    },
  });
};

export const useQueryMyWorkspaces = <TData = ListWorkspacesResponse>(
  request: Omit<ListWorkspacesRequest, `pageToken`> = {},
  options?: Omit<
    UseInfiniteQueryOptions<ListWorkspacesResponse, unknown, TData>,
    `getNextPageToken` | `queryKey` | `queryFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.workspacesMy(request),
    queryFn: ({ pageParam, signal }) => listWorkspaces(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

export const useQueryAllWorkspaces = <TData = ListWorkspacesResponse>(
  request: Omit<ListAllWorkspacesRequest, `pageToken`> = {},
  options?: Omit<
    UseInfiniteQueryOptions<ListAllWorkspacesResponse, unknown, TData>,
    `getNextPageToken` | `queryKey` | `queryFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.workspacesAll(request),
    queryFn: ({ pageParam, signal }) => listAllWorkspaces(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

export const getQueryOptionsWorkspace = <TData = Workspace>(
  authzUrl: string,
  workspaceId: string,
  queryClient?: QueryClient // if provided, will search query cache for the initial data, which might speed up the loading significantly
): UseQueryOptions<Workspace, unknown, TData> => {
  const wsName = workspaceId && workspaceName.compile({ workspace: workspaceId });
  return {
    /**
     * This optimizes loading of workspace detail - it searches in cached ListWorkspaceResponses for the workspace name.
     * If found, the cached workspace is returned synchronously so the query has data from beginning, but it still runs
     * the query in the background to ensure the data is fresh.
     */
    initialData:
      queryClient &&
      (() => {
        const cachedResponseSources = [
          ...queryClient.getQueriesData<{ pages: ListWorkspacesResponse[] }>({
            queryKey: cacheKeys.workspacesAll(),
            exact: false,
          }),
          ...queryClient.getQueriesData<{ pages: ListWorkspacesResponse[] }>({
            queryKey: cacheKeys.workspacesMy(),
            exact: false,
          }),
        ];
        for (const [, queryData] of cachedResponseSources) {
          if (queryData?.pages) {
            for (const cachedResponse of queryData.pages) {
              const cachedWorkspace = cachedResponse?.workspaces?.find((ws) => ws.name === wsName);
              if (cachedResponse) return cachedWorkspace;
            }
          }
        }
        return undefined;
      }),
    queryKey: cacheKeys.workspaceOne(workspaceId),
    queryFn: async ({ signal }) => {
      const response = await getWorkspace(authzUrl, wsName, { signal });
      return response.workspace!;
    },
  };
};

export const useQueryWorkspace = <TData = Workspace>(
  workspaceId: string | undefined,
  options?: Omit<UseQueryOptions<Workspace, unknown, TData>, `queryKey` | `queryFn` | `initialData`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  const queries = !workspaceId
    ? []
    : [
        {
          ...getQueryOptionsWorkspace<TData>(authzUrl, workspaceId, queryClient),
          ...options,
        },
      ];
  return useQueries({ queries }).at(0);
};
